import type { PlannerAssignment, PlannerRole, PlannerServicePlan } from './useChurchPlanner';

type PlannerRoleTeamCandidate = Pick<PlannerRole, 'area' | 'name'>;

export const PLANNER_TEAM_DEFINITIONS: Array<{
  id: string;
  label: string;
  aliases: string[];
}> = [
  { id: 'production', label: 'Production Team', aliases: ['production'] },
  { id: 'band', label: 'Band Team', aliases: ['band'] },
  { id: 'praise', label: 'Praise Team', aliases: ['praise', 'worship'] },
  { id: 'usher', label: 'Usher Team', aliases: ['usher', 'ushers'] },
  { id: 'deacons', label: 'Deacons Team', aliases: ['deacon', 'deacons'] },
  { id: 'elder', label: 'Elder Team', aliases: ['elder', 'elders'] },
  { id: 'altar', label: 'Altar Team', aliases: ['altar'] },
  { id: 'fine-arts', label: 'Fine Arts Team', aliases: ['fine arts', 'fine-arts', 'fine_arts', 'finearts'] },
  { id: 'greeters', label: 'Greeters Team', aliases: ['greeter', 'greeters'] },
  { id: 'security', label: 'Security Team', aliases: ['security'] },
  { id: 'stewardship', label: 'Stewardship Team', aliases: ['stewardship'] },
  { id: 'medical', label: 'Medical Team', aliases: ['medical'] },
];

export interface PlannerRoleHealthMetric {
  roleId: string;
  label: string;
  totalAssignments: number;
  filledAssignments: number;
  coverage: number;
}

function firstRelation<T>(value: T | T[] | null | undefined): T | null {
  if (!value) return null;
  return Array.isArray(value) ? value[0] ?? null : value;
}

function normalizeStatus(status: string | null | undefined): string {
  return status?.trim().toLowerCase() || 'pending';
}

function isFilledAssignment(assignment: PlannerAssignment): boolean {
  if (!assignment.user_id) return false;
  const normalized = normalizeStatus(assignment.status);
  return normalized !== 'declined' && normalized !== 'unavailable';
}

function normalizeToken(value: string | null | undefined): string {
  return (value || '')
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ');
}

export function getPlannerTeamDefinition(teamId?: string | null) {
  const normalizedTeamId = (teamId || '').trim().toLowerCase();
  if (!normalizedTeamId) return null;
  return PLANNER_TEAM_DEFINITIONS.find((team) => team.id === normalizedTeamId) ?? null;
}

export function formatPlannerTeamLabel(value?: string | null): string {
  const trimmed = (value || '').trim();
  if (!trimmed) return 'Other';
  return trimmed.replace(/[_-]+/g, ' ').replace(/\s+/g, ' ');
}

export function resolveTeamIdFromRole(role?: PlannerRoleTeamCandidate | null): string | null {
  if (!role) return null;

  const candidates = [normalizeToken(role.area), normalizeToken(role.name)].filter(Boolean);
  if (!candidates.length) return null;

  for (const team of PLANNER_TEAM_DEFINITIONS) {
    const matched = team.aliases.some((alias) => {
      const normalizedAlias = normalizeToken(alias);
      return candidates.some(
        (candidate) => candidate === normalizedAlias || candidate.includes(normalizedAlias)
      );
    });
    if (matched) return team.id;
  }

  return null;
}

export function getPlannerRoleTeamGroup(role?: PlannerRoleTeamCandidate | null): {
  key: string;
  label: string;
  teamId: string | null;
} {
  const teamId = resolveTeamIdFromRole(role);
  const definition = getPlannerTeamDefinition(teamId);
  const fallbackSource = role?.area || role?.name || null;
  const fallbackLabel = formatPlannerTeamLabel(fallbackSource);
  const fallbackKey = normalizeToken(fallbackSource) || 'other';

  return {
    key: teamId ? `team:${teamId}` : `area:${fallbackKey}`,
    label: definition?.label || fallbackLabel,
    teamId,
  };
}

export function buildPlannerRoleHealthMetrics(
  plans: PlannerServicePlan[],
  activeRoles: PlannerRole[] = []
): PlannerRoleHealthMetric[] {
  const roleMap = new Map<string, PlannerRoleHealthMetric>();

  activeRoles.forEach((role) => {
    const roleId = role.id;
    if (!roleId || roleMap.has(roleId)) return;

    roleMap.set(roleId, {
      roleId,
      label: role.name?.trim() || 'Unassigned role',
      totalAssignments: 0,
      filledAssignments: 0,
      coverage: 0,
    });
  });

  plans.forEach((plan) => {
    (plan.service_plan_assignments ?? []).forEach((assignment) => {
      const role = firstRelation(assignment.service_plan_roles);
      const roleId = assignment.role_id || role?.id;
      if (!roleId) return;

      const roleLabel = role?.name?.trim() || 'Unassigned role';
      const existing = roleMap.get(roleId);

      if (!existing) {
        roleMap.set(roleId, {
          roleId,
          label: roleLabel,
          totalAssignments: 1,
          filledAssignments: isFilledAssignment(assignment) ? 1 : 0,
          coverage: 0,
        });
        return;
      }

      existing.totalAssignments += 1;
      if (isFilledAssignment(assignment)) {
        existing.filledAssignments += 1;
      }
    });
  });

  return Array.from(roleMap.values())
    .map((metric) => ({
      ...metric,
      coverage:
        metric.totalAssignments > 0
          ? Math.round((metric.filledAssignments / metric.totalAssignments) * 100)
          : 0,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

export function buildPlannerTeamHealthMetrics(
  plans: PlannerServicePlan[],
  activeRoles: PlannerRole[] = []
): PlannerRoleHealthMetric[] {
  const teamMap = new Map<string, PlannerRoleHealthMetric>(
    PLANNER_TEAM_DEFINITIONS.map((team) => [
      team.id,
      {
        roleId: team.id,
        label: team.label,
        totalAssignments: 0,
        filledAssignments: 0,
        coverage: 0,
      },
    ])
  );

  const roleToTeam = new Map<string, string>();
  activeRoles.forEach((role) => {
    const teamId = resolveTeamIdFromRole(role);
    if (!teamId || !role.id) return;
    roleToTeam.set(role.id, teamId);
  });

  plans.forEach((plan) => {
    (plan.service_plan_assignments ?? []).forEach((assignment) => {
      const role = firstRelation(assignment.service_plan_roles);
      const teamIdFromRoleId = assignment.role_id ? roleToTeam.get(assignment.role_id) : null;
      const teamId = teamIdFromRoleId || resolveTeamIdFromRole(role);
      if (!teamId) return;

      const teamMetric = teamMap.get(teamId);
      if (!teamMetric) return;

      teamMetric.totalAssignments += 1;
      if (isFilledAssignment(assignment)) {
        teamMetric.filledAssignments += 1;
      }
    });
  });

  return PLANNER_TEAM_DEFINITIONS.map((team) => {
    const metric = teamMap.get(team.id);
    if (!metric) {
      return {
        roleId: team.id,
        label: team.label,
        totalAssignments: 0,
        filledAssignments: 0,
        coverage: 0,
      };
    }

    return {
      ...metric,
      coverage:
        metric.totalAssignments > 0
          ? Math.round((metric.filledAssignments / metric.totalAssignments) * 100)
          : 0,
    };
  });
}

export function buildOverallServiceHealth(metrics: PlannerRoleHealthMetric[]): number {
  const totals = metrics.reduce(
    (acc, metric) => {
      acc.total += metric.totalAssignments;
      acc.filled += metric.filledAssignments;
      return acc;
    },
    { total: 0, filled: 0 }
  );

  if (!totals.total) return 0;
  return Math.round((totals.filled / totals.total) * 100);
}
